import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
    FaHeart,
    FaRegHeart,
    FaMapMarkerAlt,
    FaStar,
    FaStarHalfAlt,
    FaRegStar,
    FaArrowRight,
} from "react-icons/fa";

export default function TopRatedHotels({ limit = 4 }) {
    const [hotels, setHotels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [wishlist, setWishlist] = useState([]);

    const navigate = useNavigate();

    // =========================================================
    // WISHLIST
    // =========================================================

    useEffect(() => {
        const storedWishlist =
            JSON.parse(localStorage.getItem("wishlist")) || [];

        setWishlist(storedWishlist);
    }, []);

    const updateWishlist = (updated) => {
        setWishlist(updated);

        localStorage.setItem(
            "wishlist",
            JSON.stringify(updated)
        );
    };

    const toggleWishlist = (hotel) => {
        const isLoggedIn = true; // replace later with auth

        if (!isLoggedIn) {
            alert("Please login to add to wishlist");
            return;
        }

        const exists = wishlist.find(
            (item) => item.id === hotel.id
        );

        let updated;

        if (exists) {
            updated = wishlist.filter(
                (item) => item.id !== hotel.id
            );
        } else {
            updated = [...wishlist, hotel];
        }

        updateWishlist(updated);
    };

    const isWishlisted = (id) => {
        return wishlist.some(
            (item) => item.id === id
        );
    };

    // =========================================================
    // FETCH TOP RATED HOTELS
    // =========================================================

    useEffect(() => {
        const fetchTopHotels = async () => {
            try {
                const res = await axios.get(
                    "https://beachhoteldubai.com/api/top-rated-hotels"
                );

                setHotels(res.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchTopHotels();
    }, []);

    // =========================================================
    // RENDER STARS
    // =========================================================

    const renderStars = (rating) => {
        const stars = [];
        const safeRating = Number(rating) || 0;

        for (let i = 0; i < 5; i++) {
            if (i < Math.floor(safeRating)) {
                stars.push(
                    <FaStar
                        key={i}
                        className="text-[#D7A74A]"
                    />
                );
            } else if (
                i === Math.floor(safeRating) &&
                safeRating % 1 >= 0.5
            ) {
                stars.push(
                    <FaStarHalfAlt
                        key={i}
                        className="text-[#D7A74A]"
                    />
                );
            } else {
                stars.push(
                    <FaRegStar
                        key={i}
                        className="text-[#D9E0DF]"
                    />
                );
            }
        }

        return stars;
    };

    // =========================================================
    // GET DISCOUNT
    // =========================================================

    const getDiscount = (hotel) => {
        if (hotel.discount) {
            return hotel.discount;
        }

        if (hotel.discount_percentage) {
            return hotel.discount_percentage;
        }

        return null;
    };

    // =========================================================
    // LOADING
    // =========================================================

    if (loading) {
        return (
            <section
                className="
                    w-full
                    bg-[#F3F8FF]

                    px-4
                    py-14

                    sm:px-6
                    sm:py-16

                    lg:px-10
                    lg:py-20
                "
            >
                <div
                    className="
                        mx-auto
                        w-full
                        max-w-[1380px]
                    "
                >
                    {/* Heading Skeleton */}

                    <div
                        className="
                            mb-10
                            flex
                            flex-col
                            gap-4

                            lg:flex-row
                            lg:items-end
                            lg:justify-between
                        "
                    >
                        <div className="space-y-3">
                            <div
                                className="
                                    h-3
                                    w-28
                                    rounded-full
                                    bg-[#DDE8F1]
                                    animate-pulse
                                "
                            />

                            <div
                                className="
                                    h-10
                                    w-72
                                    rounded-lg
                                    bg-[#DDE8F1]
                                    animate-pulse
                                "
                            />

                            <div
                                className="
                                    h-3
                                    w-96
                                    max-w-full
                                    rounded-full
                                    bg-[#DDE8F1]
                                    animate-pulse
                                "
                            />
                        </div>

                        <div
                            className="
                                h-11
                                w-36
                                rounded-full
                                bg-[#DDE8F1]
                                animate-pulse
                            "
                        />
                    </div>

                    {/* Cards Skeleton */}

                    <div
                        className="
                            grid
                            grid-cols-1
                            gap-6

                            sm:grid-cols-2

                            xl:grid-cols-4
                        "
                    >
                        {[1, 2, 3, 4].map((item) => (
                            <div
                                key={item}
                                className="
                                    overflow-hidden
                                    rounded-[24px]
                                    border
                                    border-[#E0E8EF]
                                    bg-white
                                "
                            >
                                <div
                                    className="
                                        h-[230px]
                                        bg-[#DDE8F1]
                                        animate-pulse
                                    "
                                />

                                <div
                                    className="
                                        space-y-4
                                        p-5
                                    "
                                >
                                    <div
                                        className="
                                            h-5
                                            rounded
                                            bg-[#DDE8F1]
                                            animate-pulse
                                        "
                                    />

                                    <div
                                        className="
                                            h-3
                                            w-2/3
                                            rounded
                                            bg-[#DDE8F1]
                                            animate-pulse
                                        "
                                    />

                                    <div
                                        className="
                                            h-6
                                            w-1/2
                                            rounded
                                            bg-[#DDE8F1]
                                            animate-pulse
                                        "
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    // =========================================================
    // RENDER
    // =========================================================

    return (
        <section
            className="
                relative
                w-full
                overflow-hidden

                bg-[#F3F8FF]

                px-4
                pb-14

                sm:px-6
                sm:pb-16

                lg:px-10
                lg:pb-20

                xl:pb-24
            "
        >
            {/* =====================================================
                DECORATIVE BACKGROUND
            ===================================================== */}

            <div
                className="
                    pointer-events-none
                    absolute
                    -right-32
                    top-10
                    h-[380px]
                    w-[380px]
                    rounded-full
                    border
                    border-[#D7A74A]/20
                "
            />

            <div
                className="
                    pointer-events-none
                    absolute
                    -left-40
                    bottom-0
                    h-[420px]
                    w-[420px]
                    rounded-full
                    border
                    border-[#0B5552]/10
                "
            />

            <div
                className="
                    pointer-events-none
                    absolute
                    right-20
                    top-24
                    h-2
                    w-2
                    rounded-full
                    bg-[#D7A74A]
                "
            />

            <div
                className="
                    pointer-events-none
                    absolute
                    left-24
                    bottom-24
                    h-2
                    w-2
                    rounded-full
                    bg-[#8B0000]
                "
            />

            <div
                className="
                    relative
                    z-10
                    mx-auto
                    w-full
                    max-w-[1380px]
                "
            >

                {/* =================================================
                    SECTION HEADER
                ================================================= */}

                <div
                    className="
                        mb-10

                        flex
                        flex-col
                        gap-6

                        lg:mb-12
                        lg:flex-row
                        lg:items-end
                        lg:justify-between
                    "
                >

                    {/* LEFT */}

                    <div className="max-w-[650px]">

                        {/* Eyebrow */}

                        <div
                            className="
                                mb-4
                                flex
                                items-center
                                gap-3
                            "
                        >
                            <span
                                className="
                                    h-[2px]
                                    w-8
                                    bg-[#8B0000]
                                "
                            />

                            <span
                                className="
                                    text-[10px]
                                    font-bold
                                    uppercase
                                    tracking-[0.22em]
                                    text-[#0B5552]
                                "
                            >
                                Featured Stays
                            </span>
                        </div>

                        {/* Heading */}

                        <h2
                            className="
                                font-serif
                                text-[32px]
                                font-medium
                                leading-[1.05]
                                tracking-[-0.03em]
                                text-[#0B5552]

                                sm:text-[38px]

                                md:text-[44px]

                                lg:text-[48px]
                            "
                        >
                            Discover Dubai's
                            <span
                                className="
                                    ml-2
                                    italic
                                    text-[#8B0000]
                                "
                            >
                                finest stays.
                            </span>
                        </h2>

                        {/* Description */}

                        <p
                            className="
                                mt-4
                                max-w-[590px]

                                text-[13px]
                                leading-[1.8]
                                text-[#657B82]

                                sm:text-[14px]
                            "
                        >
                            Explore handpicked hotels offering
                            exceptional comfort, beautiful
                            locations and memorable experiences
                            across Dubai.
                        </p>
                    </div>

                    {/* VIEW ALL */}

                    <button
                        type="button"
                        onClick={() =>
                            navigate("/hotels")
                        }
                        className="
                            group
                            inline-flex
                            w-fit
                            shrink-0
                            items-center
                            gap-3

                            rounded-full

                            border
                            border-[#0B5552]/20

                            bg-white

                            px-6
                            py-3.5

                            text-[11px]
                            font-bold
                            uppercase
                            tracking-[0.08em]
                            text-[#0B5552]

                            shadow-[0_8px_25px_rgba(11,85,82,0.07)]

                            transition-all
                            duration-300

                            hover:-translate-y-0.5
                            hover:border-[#D7A74A]
                            hover:bg-[#D7A74A]
                            hover:text-[#0B5552]
                            hover:shadow-[0_12px_30px_rgba(215,167,74,0.18)]

                            active:scale-[0.98]
                        "
                    >
                        <span>
                            View All Hotels
                        </span>

                        <FaArrowRight
                            className="
                                text-[10px]

                                transition-transform
                                duration-300

                                group-hover:translate-x-1
                            "
                        />
                    </button>
                </div>

                {/* =================================================
                    HOTEL GRID
                ================================================= */}

                <div
                    className="
                        grid
                        grid-cols-1
                        gap-6

                        sm:grid-cols-2

                        xl:grid-cols-4
                    "
                >
                    {hotels
                        .slice(0, limit)
                        .map((hotel, index) => {

                            const discount =
                                getDiscount(hotel);

                            return (
                                <article
                                    key={hotel.id}
                                    className="
                                        group
                                        relative

                                        overflow-hidden

                                        rounded-[24px]

                                        border
                                        border-[#DDE7EE]

                                        bg-white

                                        shadow-[0_8px_30px_rgba(11,85,82,0.07)]

                                        transition-all
                                        duration-500

                                        hover:-translate-y-2
                                        hover:shadow-[0_20px_45px_rgba(11,85,82,0.14)]
                                    "
                                >

                                    {/* =================================
                                        IMAGE
                                    ================================= */}

                                    <div
                                        className="
                                            relative
                                            h-[235px]
                                            cursor-pointer
                                            overflow-hidden

                                            sm:h-[220px]

                                            lg:h-[225px]
                                        "
                                        onClick={() =>
                                            navigate(
                                                `/hotels/${hotel.slug}`
                                            )
                                        }
                                    >

                                        <img
                                            src={`https://uaediscountedhotels.com/uploads/hotels/${hotel.image}`}
                                            alt={hotel.name}
                                            className="
                                                h-full
                                                w-full
                                                object-cover

                                                transition-transform
                                                duration-700
                                                ease-out

                                                group-hover:scale-[1.07]
                                            "
                                        />

                                        {/* Image Overlay */}

                                        <div
                                            className="
                                                pointer-events-none
                                                absolute
                                                inset-0

                                                bg-gradient-to-t
                                                from-[#062F3A]/65
                                                via-[#062F3A]/5
                                                to-transparent
                                            "
                                        />

                                        {/* TOP RATED */}

                                        {index === 0 && (
                                            <div
                                                className="
                                                    absolute
                                                    left-4
                                                    top-4

                                                    flex
                                                    items-center
                                                    gap-2

                                                    rounded-full

                                                    bg-[#D7A74A]

                                                    px-3.5
                                                    py-1.5

                                                    text-[9px]
                                                    font-bold
                                                    uppercase
                                                    tracking-[0.08em]
                                                    text-[#0B5552]

                                                    shadow-[0_5px_15px_rgba(0,0,0,0.12)]
                                                "
                                            >
                                                <FaStar
                                                    className="text-[9px]"
                                                />

                                                Top Rated
                                            </div>
                                        )}

                                        {/* DISCOUNT */}

                                        {discount && (
                                            <div
                                                className="
                                                    absolute
                                                    bottom-4
                                                    left-4

                                                    rounded-full

                                                    bg-[#8B0000]

                                                    px-3.5
                                                    py-1.5

                                                    text-[10px]
                                                    font-bold
                                                    text-white

                                                    shadow-[0_5px_15px_rgba(139,0,0,0.25)]
                                                "
                                            >
                                                {String(
                                                    discount
                                                ).includes("%")
                                                    ? `-${String(
                                                        discount
                                                    ).replace(
                                                        "%",
                                                        ""
                                                    )}% OFF`
                                                    : `-${discount}% OFF`}
                                            </div>
                                        )}

                                        {/* WISHLIST */}

                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();

                                                toggleWishlist(
                                                    hotel
                                                );
                                            }}
                                            className="
                                                absolute
                                                right-4
                                                top-4

                                                flex
                                                h-10
                                                w-10
                                                items-center
                                                justify-center

                                                rounded-full

                                                bg-white/95

                                                shadow-[0_6px_18px_rgba(0,0,0,0.15)]

                                                backdrop-blur-sm

                                                transition-all
                                                duration-300

                                                hover:scale-110
                                            "
                                            aria-label={
                                                isWishlisted(
                                                    hotel.id
                                                )
                                                    ? "Remove from wishlist"
                                                    : "Add to wishlist"
                                            }
                                        >
                                            {isWishlisted(
                                                hotel.id
                                            ) ? (
                                                <FaHeart
                                                    className="
                                                        text-[15px]
                                                        text-[#8B0000]
                                                    "
                                                />
                                            ) : (
                                                <FaRegHeart
                                                    className="
                                                        text-[15px]
                                                        text-[#0B5552]
                                                    "
                                                />
                                            )}
                                        </button>

                                        {/* IMAGE BOTTOM INFO */}

                                        <div
                                            className="
                                                absolute
                                                bottom-4
                                                right-4
                                            "
                                        >
                                            <div
                                                className="
        rounded-full
        bg-white/95

        px-3
        py-1.5

        text-[10px]
        font-bold
        text-[#0B5552]

        shadow-md
        backdrop-blur-sm
    "
                                            >
                                                {hotel.destination || hotel.location || hotel.city || "Location"}
                                            </div>
                                        </div>
                                    </div>

                                    {/* =================================
                                        CONTENT
                                    ================================= */}

                                    <div className="p-5">

                                        {/* HOTEL NAME */}

                                        <h3
                                            onClick={() =>
                                                navigate(
                                                    `/hotels/${hotel.slug}`
                                                )
                                            }
                                            title={hotel.name}
                                            className="
                                                cursor-pointer

                                                truncate

                                                font-serif
                                                text-[19px]
                                                font-medium
                                                leading-tight
                                                text-[#0B5552]

                                                transition-colors
                                                duration-200

                                                hover:text-[#8B0000]
                                            "
                                        >
                                            {hotel.name}
                                        </h3>

                                        {/* LOCATION */}

                                        <div
                                            className="
                                                mt-3

                                                flex
                                                min-w-0
                                                items-center
                                                gap-2
                                            "
                                        >
                                            <div
                                                className="
                                                    flex
                                                    h-6
                                                    w-6
                                                    shrink-0
                                                    items-center
                                                    justify-center

                                                    rounded-full

                                                    bg-[#F3F8FF]
                                                "
                                            >
                                                <FaMapMarkerAlt
                                                    className="
                                                        text-[10px]
                                                        text-[#8B0000]
                                                    "
                                                />
                                            </div>

                                            <span
                                                title={
                                                    hotel.location
                                                }
                                                className="
                                                    truncate

                                                    text-[11px]
                                                    font-medium
                                                    text-[#71858A]
                                                "
                                            >
                                                {
                                                    hotel.location
                                                }
                                            </span>
                                        </div>

                                        {/* DIVIDER */}

                                        <div
                                            className="
                                                my-4
                                                h-px
                                                w-full
                                                bg-[#E8EEF2]
                                            "
                                        />

                                        {/* RATING */}

                                        <div
                                            className="
                                                flex
                                                items-center
                                                justify-between
                                            "
                                        >
                                            <div
                                                className="
                                                    flex
                                                    items-center
                                                    gap-2
                                                "
                                            >
                                                <div
                                                    className="
                                                        flex
                                                        items-center
                                                        gap-[2px]
                                                        text-[11px]
                                                    "
                                                >
                                                    {renderStars(
                                                        hotel.rating
                                                    )}
                                                </div>

                                                <span
                                                    className="
                                                        text-[11px]
                                                        font-bold
                                                        text-[#0B5552]
                                                    "
                                                >
                                                    {hotel.rating ||
                                                        "0"}
                                                </span>
                                            </div>

                                            <span
                                                className="
                                                    text-[10px]
                                                    font-medium
                                                    uppercase
                                                    tracking-[0.06em]
                                                    text-[#9AA9AD]
                                                "
                                            >
                                                Guest Rating
                                            </span>
                                        </div>

                                        {/* PRICE */}

                                        <div
                                            className="
                                                mt-5
                                                flex
                                                items-end
                                                justify-between
                                                gap-3
                                            "
                                        >
                                            <div>
                                                <span
                                                    className="
                                                        block
                                                        text-[9px]
                                                        font-medium
                                                        uppercase
                                                        tracking-[0.12em]
                                                        text-[#8A999D]
                                                    "
                                                >
                                                    Starting from
                                                </span>

                                                <div
                                                    className="
                                                        mt-1
                                                        flex
                                                        items-baseline
                                                        gap-1.5
                                                    "
                                                >
                                                    <span
                                                        className="
                                                            font-serif
                                                            text-[21px]
                                                            font-semibold
                                                            leading-none
                                                            text-[#0B5552]
                                                        "
                                                    >
                                                        AED{" "}
                                                        {Math.round(
                                                            hotel.price_per_night
                                                        )}
                                                    </span>

                                                    <span
                                                        className="
                                                            text-[10px]
                                                            text-[#7E8C90]
                                                        "
                                                    >
                                                        / night
                                                    </span>
                                                </div>
                                            </div>

                                            {/* ARROW */}

                                            <button
                                                type="button"
                                                onClick={() =>
                                                    navigate(
                                                        `/hotels/${hotel.slug}`
                                                    )
                                                }
                                                className="
                                                    group/arrow

                                                    flex
                                                    h-10
                                                    w-10
                                                    shrink-0
                                                    items-center
                                                    justify-center

                                                    rounded-full

                                                    bg-[#8B0000]

                                                    text-white

                                                    shadow-[0_6px_15px_rgba(11,85,82,0.18)]

                                                    transition-all
                                                    duration-300

                                                    hover:bg-[#0B5552]
                                                    hover:shadow-[0_8px_20px_rgba(139,0,0,0.22)]
                                                "
                                                aria-label={`View ${hotel.name}`}
                                            >
                                                <FaArrowRight
                                                    className="
                                                        text-[11px]

                                                        transition-transform
                                                        duration-300

                                                        group-hover/arrow:translate-x-0.5
                                                    "
                                                />
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                </div>

                {/* =================================================
                    BOTTOM TRUST LINE
                ================================================= */}

                <div
                    className="
                        mt-10
                        flex
                        flex-wrap
                        items-center
                        justify-center
                        gap-x-6
                        gap-y-3

                        border-t
                        border-[#DDE7EE]

                        pt-7

                        text-center
                    "
                >
                    <span
                        className="
                            text-[10px]
                            font-semibold
                            uppercase
                            tracking-[0.12em]
                            text-[#71858A]
                        "
                    >
                        Luxury stays
                    </span>

                    <span
                        className="
                            h-1
                            w-1
                            rounded-full
                            bg-[#D7A74A]
                        "
                    />

                    <span
                        className="
                            text-[10px]
                            font-semibold
                            uppercase
                            tracking-[0.12em]
                            text-[#71858A]
                        "
                    >
                        Best hotel rates
                    </span>

                    <span
                        className="
                            h-1
                            w-1
                            rounded-full
                            bg-[#D7A74A]
                        "
                    />

                    <span
                        className="
                            text-[10px]
                            font-semibold
                            uppercase
                            tracking-[0.12em]
                            text-[#71858A]
                        "
                    >
                        Dubai's finest hotels
                    </span>
                </div>
            </div>
        </section>
    );
}